<template>
  <v-container>
    <v-row>
      <v-col>
        <v-text-field
          :label="$t('documentsView.searchTerm')"
          v-model="query"
          append-icon="mdi-magnify"
          outlined
          clearable
        >
        </v-text-field>
      </v-col>
      <v-col cols="2" v-if="!isMagazineView">
        <v-select
          v-model="categoryFilter"
          :items="categories"
          :label="$t('form.fields.category.category')"
          outlined
        ></v-select>
      </v-col>
      <v-col cols="1" v-if="isAdministrator && !isMagazineView">
        <v-btn
          class="mt-1"
          tile
          large
          color="primary"
          icon
          @click="showDocumentEditForm({})"
        >
          <v-icon>mdi-file-plus</v-icon>
        </v-btn>
      </v-col>
    </v-row>
    <v-row class="mt-n8">
      <v-col>
        <v-data-table
          :headers="headers"
          :items="filteredDocuments"
          :search="query"
          :loading="loading"
          :footer-props="dataTableFooterProps"
          :options="{
            sortBy: ['date'],
            sortDesc: [true],
          }"
        >
          <template
            #[`footer.page-text`]="{ pageStart, pageStop, itemsLength }"
          >
            {{ $t("pageText", { pageStart, pageStop, itemsLength }) }}
          </template>
          <template #[`item.date`]="{ item }">
            {{ item.date | formatDate }}
          </template>
          <template #[`item.documents`]="{ item }">
            <span v-for="document in item.documents" :key="document._id">
              <v-chip
                close
                color="primary"
                text-color="white"
                close-icon="mdi-download"
                @click="downloadDocument(document.document)"
                @click:close="downloadDocument(document.document)"
                class="mt-1 mb-1 mr-2"
              >
                <v-avatar left>
                  <v-icon small>mdi-file</v-icon>
                </v-avatar>
                {{ document.title }}
              </v-chip>
            </span>
          </template>
          <template #[`item.userGroups`]="{ item }">
            <span
              v-for="userGroup in item.userGroups.split(',')"
              :key="userGroup"
            >
              <v-chip color="primary" text-color="white" class="mt-1 mb-1 mr-2">
                {{ $t(`groups.${userGroup}`) }}
              </v-chip>
            </span>
          </template>
          <template #[`item.actions`]="{ item }">
            <v-btn
              tile
              large
              color="primary"
              icon
              @click="showDocumentEditForm(item)"
            >
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <v-btn
              tile
              large
              color="primary"
              icon
              @click="showDocumentDeleteConfirmationForm(item)"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <v-dialog v-model="editDocumentDialog" max-width="800px" persistent>
      <v-card>
        <v-card-title class="white--text headline primary">
          {{ $t(`documentsView.${editDocument._id ? "edit" : "add"}Document`) }}
          <v-spacer></v-spacer>
          <v-btn icon dark @click="closeDocumentEditForm">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-form :lazy-validation="true">
            <validation-observer ref="validationObserver" v-slot="{ invalid }">
              <v-row class="mt-2">
                <v-col>
                  <validation-provider
                    :name="$t('form.fields.title')"
                    v-slot="{ errors }"
                    rules="required"
                  >
                    <v-text-field
                      v-model="editDocument.title"
                      :label="$t('form.fields.title') + ' *'"
                      required
                      outlined
                      :error-messages="errors"
                    ></v-text-field>
                  </validation-provider>
                </v-col>
                <v-col cols="4">
                  <v-menu
                    ref="showDatePicker"
                    v-model="showDatePicker"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    max-width="290px"
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <validation-provider
                        :name="$t('form.fields.date')"
                        v-slot="{ errors }"
                        rules="required"
                      >
                        <v-text-field
                          :value="formattedTimeValue"
                          :label="$t('form.fields.date') + ' *'"
                          prepend-icon="mdi-calendar"
                          required
                          outlined
                          :error-messages="errors"
                          readonly
                          v-on="on"
                        >
                        </v-text-field>
                      </validation-provider>
                    </template>
                    <v-date-picker
                      v-model="editDocument.date"
                      no-title
                      @input="showDatePicker = false"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>
              <v-row class="mt-n8">
                <v-col>
                  <v-combobox
                    v-model="userGroups"
                    :items="allGroups"
                    :label="$t('form.fields.groups')"
                    multiple
                    outlined
                    :disabled="!isAdministrator"
                  >
                    <template v-slot:item="{ item, attrs }">
                      <v-checkbox
                        v-model="attrs.inputValue"
                        :label="$t(`groups.${item}`)"
                        color="primary"
                      ></v-checkbox>
                    </template>
                    <template v-slot:selection="{ attrs, item, selected }">
                      <v-chip
                        v-bind="attrs"
                        :input-value="selected"
                        close
                        @click:close="removeGroup(item)"
                      >
                        {{ $t(`groups.${item}`) }}
                      </v-chip>
                    </template>
                  </v-combobox>
                </v-col>
                <v-col>
                  <v-select
                    v-model="editDocument.category"
                    :items="categories"
                    :label="$t('form.fields.category.category')"
                    outlined
                    :disabled="!isAdministrator"
                  ></v-select>
                </v-col>
              </v-row>
              <v-row class="mt-n8">
                <v-col>
                  <v-file-input
                    v-model="newDocuments"
                    accept=".png,.jpg,.jpeg,.pdf,.doc,.docx,.zip,.xls,.xlsx"
                    :label="$t('form.fields.documents')"
                    :loading="uploadingDocument"
                    multiple
                    small-chips
                    outlined
                    prepend-icon="mdi-paperclip"
                  ></v-file-input>
                </v-col>
              </v-row>
              <v-row
                class="mt-n8"
                v-if="
                  editDocument.documents && editDocument.documents.length > 0
                "
              >
                <v-col>
                  <span
                    v-for="document in editDocument.documents"
                    :key="document._id"
                  >
                    <v-chip
                      class="ma-2"
                      close
                      color="primary"
                      text-color="white"
                      close-icon="mdi-download"
                      @click:close="downloadDocument(document.document)"
                    >
                      <v-avatar left>
                        <v-icon small>mdi-file</v-icon>
                      </v-avatar>
                      {{ document.title }}
                      <v-icon right @click="deleteDocumentFile(document)">
                        mdi-delete
                      </v-icon>
                    </v-chip>
                  </span>
                </v-col>
              </v-row>
              <v-row class="mt-n8" v-if="sendMail">
                <v-col cols="12">
                  <v-text-field
                    v-model="editDocument.mailSubject"
                    :label="$t('documentsView.mailSubject')"
                    outlined
                  ></v-text-field>
                </v-col>
                <v-col class="mt-n8">
                  <v-textarea
                    v-model="editDocument.mailText"
                    :label="$t('documentsView.mailText')"
                    outlined
                  ></v-textarea>
                </v-col>
              </v-row>
              <v-card-actions class="mt-n8">
                <v-switch
                  v-model="sendMail"
                  :label="$t('documentsView.sendMail')"
                ></v-switch>
                <v-spacer></v-spacer>
                <v-btn
                  color="primary"
                  :disabled="invalid || !canSaveDocument()"
                  :loading="uploadingDocument"
                  @click="saveDocument()"
                >
                  {{ $t("form.btn.save") }}
                </v-btn>
              </v-card-actions>
            </validation-observer>
          </v-form>
        </v-card-text>
        <v-divider></v-divider>
      </v-card>
    </v-dialog>
    <v-dialog v-model="deleteDocumentDialog" persistent max-width="300px">
      <v-card>
        <v-card-title class="white--text headline primary">
          {{ $t("documentsView.deleteDocument") }}
        </v-card-title>
        <v-card-text
          class="title pt-5"
          v-html="
            $t('documentsView.deleteDocumentText', {
              title: editDocument.title,
            })
          "
        >
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="deleteDocument()">
            {{ $t("yes") }}
          </v-btn>
          <v-btn color="primary" text @click="deleteDocumentDialog = false">
            {{ $t("no") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import { i18n } from "../plugins/i18n";
import {
  documentsResource,
  documentsMailResource,
  attachmentResource,
} from "../api/resources";
import { SelectionUserGroups } from "../config/constants";
import * as moment from "moment";

export default {
  data: () => ({
    query: null,
    loading: false,
    editDocumentDialog: false,
    deleteDocumentDialog: false,
    showDatePicker: false,
    sendMail: false,
    userGroups: [],
    allGroups: SelectionUserGroups.filter(
      (element) => element !== "administrator"
    ),
    editDocument: {},
    oldDocument: null,
    newDocuments: [],
    uploadingDocument: false,
    documents: [],
    categoryFilter: "",
  }),
  mounted() {
    moment.locale(i18n.locale);
    this.loadDocuments();
  },
  watch: {
    currentGroups() {
      this.loadDocuments();
    },
  },
  computed: {
    ...mapGetters(["isAdministrator", "currentGroups", "dataTableFooterProps"]),
    headers() {
      const headers = [
        { text: this.$t("documentsView.date"), value: "date" },
        { text: this.$t("documentsView.title"), value: "title" },
        { text: this.$t("documentsView.documents"), value: "documents" },
      ];
      if (this.isAdministrator) {
        headers.push(
          { text: this.$t("documentsView.userGroups"), value: "userGroups" },
          { text: "", value: "actions", sortable: false }
        );
      }
      return headers;
    },
    formattedTimeValue() {
      return moment(this.editDocument.date).format("LL");
    },
    locale() {
      return i18n.locale;
    },
    categories() {
      return [
        { value: "", text: this.$t("form.fields.category.none") },
        {
          value: "clubMagazine",
          text: this.$t("form.fields.category.clubMagazine"),
        },
      ];
    },
    filteredDocuments() {
      let categoryFilter = this.categoryFilter;
      if (this.isMagazineView) {
        categoryFilter = "clubMagazine";
      }
      if (!categoryFilter) {
        return this.documents;
      }
      return this.documents.filter(
        (document) => document.category === categoryFilter
      );
    },
    isMagazineView() {
      return this.$route.params.type === "magazines";
    },
  },
  methods: {
    showDocumentEditForm(document) {
      this.editDocument = document;
      this.sendMail = !this.editDocument._id;
      this.newDocuments = [];
      this.oldDocument = JSON.stringify(document);
      if (!this.editDocument.date) {
        this.editDocument.date = moment().format("YYYY-MM-DD");
      }
      if (!this.editDocument.documents) {
        this.editDocument.documents = [];
      }
      if (!this.editDocument.category) {
        this.editDocument.category = "";
      }
      this.userGroups = this.editDocument.userGroups
        ? this.editDocument.userGroups.split(",")
        : [];
      this.editDocumentDialog = true;
    },
    closeDocumentEditForm() {
      if (this.editDocument._id) {
        const oldDocument = JSON.parse(this.oldDocument);
        this.$set(
          this.documents,
          this.documents.findIndex(
            (document) => document._id === oldDocument._id
          ),
          oldDocument
        );
      }
      this.editDocumentDialog = false;
      this.$refs.validationObserver.reset();
    },
    showDocumentDeleteConfirmationForm(document) {
      this.editDocument = document;
      this.deleteDocumentDialog = true;
    },
    async deleteDocument() {
      await documentsResource.delete(this.editDocument._id, {
        headers: {
          "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
        },
      });
      this.documents.splice(this.documents.indexOf(this.editDocument), 1);
      this.deleteDocumentDialog = false;
    },
    downloadDocument(document) {
      let fileUrl = document._url;
      if (!fileUrl && document._urls) {
        fileUrl = document._urls.original;
      }
      window.open(fileUrl, "_blank");
    },
    removeGroup(item) {
      this.userGroups.splice(this.userGroups.indexOf(item), 1);
    },
    deleteDocumentFile(document) {
      this.editDocument.documents.splice(
        this.editDocument.documents.indexOf(document),
        1
      );
    },
    async loadDocuments() {
      this.loading = true;
      this.documents = [];
      if (this.isAdministrator) {
        this.documents = (
          await documentsResource.get("", {
            headers: {
              "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
            },
          })
        ).data.results;
      } else {
        this.currentGroups.forEach(async (group) => {
          const groupDocuments = (
            await documentsResource.get("", {
              params: { userGroups: group },
              headers: {
                "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
              },
            })
          ).data.results;
          groupDocuments.forEach((document) => {
            if (
              this.documents.findIndex((entry) => entry._id === document._id) <
              0
            ) {
              this.documents.push(document);
            }
          });
        });
      }
      this.loading = false;
    },
    async saveDocument() {
      this.uploadingDocument = true;

      for (let index in this.newDocuments) {
        const fileData = this.newDocuments[index];
        const formData = new FormData();
        formData.append("file", fileData);
        const data = await attachmentResource.post("", formData, {
          headers: { "x-xsrf-token": this.$cookies.get("oegg-cms.csrf") },
        });
        this.editDocument.documents.push({
          title: fileData.name,
          document: data.data,
        });
      }
      this.editDocument.userGroups = this.userGroups.join(",");

      // create new document
      if (!this.editDocument._id) {
        await documentsResource.post("", this.editDocument, {
          headers: {
            "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
          },
        });
        await this.loadDocuments();
      } else {
        await documentsResource.put(this.editDocument._id, this.editDocument, {
          headers: {
            "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
          },
        });
      }

      if (this.sendMail) {
        documentsMailResource.post(
          "",
          { mailData: { document: this.editDocument } },
          {
            headers: {
              "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
            },
          }
        );
      }

      this.uploadingDocument = false;
      this.editDocumentDialog = false;
      this.$refs.validationObserver.reset();
    },
    canSaveDocument() {
      if (this.userGroups.length === 0) {
        return false;
      }
      if (this.editDocument._id) {
        if (
          this.editDocument.documents.length + this.newDocuments.length ===
          0
        ) {
          return false;
        }
        if (this.newDocuments.length > 0) {
          return true;
        }
        if (this.userGroups.join(",") !== this.editDocument.userGroups) {
          return true;
        }
        return JSON.stringify(this.editDocument) !== this.oldDocument;
      }
      return this.newDocuments.length > 0;
    },
  },
};
</script>
